import { useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/api";
import { LogoMark } from "@/context/BrandingContext";
import { Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function ResetPassword() {
  const [sp] = useSearchParams();
  const navigate = useNavigate();
  const token = sp.get("token") || "";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault(); setError("");
    if (pw.length < 8) { setError("Wachtwoord moet minimaal 8 tekens zijn"); return; }
    if (pw !== pw2) { setError("Wachtwoorden komen niet overeen"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/reset-password", { token, password: pw });
      setDone(true); toast.success(data.message || "Wachtwoord aangepast");
    } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-900 p-6">
      <div className="w-full max-w-sm rounded-2xl bg-white p-8 shadow-xl" data-testid="reset-page">
        <div className="mb-6 flex items-center gap-3"><LogoMark size={44} rounded="rounded-xl" /><div><div className="font-display text-lg font-bold text-slate-900">DispatchMe</div><div className="text-xs uppercase tracking-widest text-slate-400">Nieuw wachtwoord</div></div></div>
        {done ? (
          <div className="text-center" data-testid="reset-done">
            <CheckCircle2 className="mx-auto mb-3 h-12 w-12 text-emerald-500" />
            <h2 className="font-display text-xl font-bold text-slate-900">Wachtwoord aangepast</h2>
            <p className="mt-2 text-sm text-slate-500">Je kunt nu inloggen met je nieuwe wachtwoord.</p>
            <button onClick={() => navigate("/login")} className="mt-5 w-full rounded-xl bg-blue-600 py-2.5 text-sm font-semibold text-white hover:bg-blue-500">Naar inloggen</button>
          </div>
        ) : !token ? (
          <div className="text-center" data-testid="reset-notoken">
            <p className="text-sm text-slate-500">Deze link is ongeldig. Vraag opnieuw een herstel-link aan.</p>
            <button onClick={() => navigate("/wachtwoord-vergeten")} className="mt-5 w-full rounded-xl bg-blue-600 py-2.5 text-sm font-semibold text-white hover:bg-blue-500">Herstel-link aanvragen</button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <p className="text-sm text-slate-500">Kies een nieuw wachtwoord voor je account.</p>
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Nieuw wachtwoord</label>
              <input data-testid="reset-password" type="password" value={pw} onChange={(e) => setPw(e.target.value)} required className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-blue-500" placeholder="Minimaal 8 tekens" />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Herhaal wachtwoord</label>
              <input data-testid="reset-password2" type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} required className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-blue-500" placeholder="••••••••" />
            </div>
            {error && <div data-testid="reset-error" className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600">{error}</div>}
            <button data-testid="reset-submit" type="submit" disabled={loading} className="flex w-full items-center justify-center gap-2 rounded-xl bg-blue-600 py-2.5 text-sm font-semibold text-white hover:bg-blue-500 disabled:opacity-60">{loading && <Loader2 className="h-4 w-4 animate-spin" />} Wachtwoord opslaan</button>
          </form>
        )}
      </div>
    </div>
  );
}
